import { useQuery } from './useQuery';
import { useEffect } from 'react';

/**
 * useFeatureFlag
 * Resolves a feature flag from a URL query param, e.g. ?workday=true.
 * Always call with a FEATURE_FLAG entry from flag registry, common/featureFlags.js.
 * 
 * - persist: false (default) -> pure query-param read, no storage touched.
 *            Use for single-page flags with no subroutes.
 * - persist: true  -> once explicitly set via the URL, "sticks" in
 *            sessionStorage so it survives client-side navigation to
 *            subpages that don't carry the query param. Disabled by an
 *            explicit ?<flag>=false, or automatically when the browser
 *            tab/window is closed.
 *
 * How it works:
 * - Reads ?<flag>=true|false from the current URL on every render.
 * - If the query param is present, it always wins for that render
 *   ('true' => true, anything else => false).
 * - When persist is enabled, the resolved value is also written to
 *   sessionStorage as a side effect, so later pages/renders that don't carry
 *   the query param can still recover the last explicitly-set value.
 * - If the query param is absent: returns the persisted sessionStorage value
 *   (when persist is true), or false (when persist is false/default).
 *
 * Example:
 *   const showWorkdayFeature = useFeatureFlag(FEATURE_FLAG.WORKDAY);
 *   // URL: /activation-engine?workday=true  => true  (persisted)
 *   // URL: /activation-engine?workday=false => false (persisted/cleared)
 *   // URL: /activation-engine (no param)    => last persisted value, or false
 */
export function useFeatureFlag({ flag, persist = false }) {
    const query = useQuery();
    const direct = query.get(flag); // 'true' | 'false' | null

    useEffect(() => {
        if (persist && (direct === 'true' || direct === 'false')) {
            if (sessionStorage.getItem(flag) !== direct) sessionStorage.setItem(flag, direct);
        }
    }, [direct, flag, persist]);

    if (direct !== null) {
        return direct === 'true';
    }

    return persist ? sessionStorage.getItem(flag) === 'true' : false;
}
